import Calculate from "@mui/icons-material/Calculate";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import {
	CircularProgress,
	Divider,
	FormHelperText,
	InputLabel,
	MenuItem,
	Stack,
	Tooltip,
} from "@mui/material";
import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";

import type { ReactionResourceOption } from "@/components/global/components/tiptap/extensions/suggestion/suggestion-types";
import type { VariableDataTypeOption } from "@/constants/rule-variables";

import messages from "../../../constants/messages";
import apiRoutes from "../../../constants/api-routes";
import useAuthHeader from "../../../hooks/useAuthHeader";
import type { Option } from "../constants";
import { DataTypeField } from "../fields/DataTypeField";
import type {
	DatabaseTableOptionV2,
	ResourceCategoryV2,
	ScreenLocationFieldOption,
	VariableGroupV2,
	VisibleGroupKey,
} from "../fields/form-entity-select";
import FormEntitySelect from "../fields/form-entity-select";
import type { ScreenLocationOption } from "../modals/CreateRuleModal";
import FormulaOperators from "../fields/FormulaOperatorsSelect";
import { FormulaTextArea, type FormulaTextAreaHandle } from "../fields/FormulaTextArea";
import type { RuleVariable } from "../hooks/useRuleVariables";
import { dedupeOptionsByKey } from "../utils/expression-metadata-utils";
import type { FormulaVariableFormulaPayload } from "../utils/formula-variable-payload";
import { normalizeVariableFormulaFromApi } from "../utils/formula-variable-payload";
import { getEditorInsertLabelForPickedResource } from "../utils/resource-insert-label-utils";
import { isVariableNameTakenInRule } from "../utils/variable-naming-utils";
import BaseForm from "./BaseForm";
import { CanvasFormTextField } from "./styled/CanvasFormTextField.styled";

export interface FormulaBuilderFormState {
	name: string;
	dataTypeId: number | null;
	variableFormula: FormulaVariableFormulaPayload;
}

export interface FormulaBuilderFormProps {
	open: boolean;
	onClose: () => void;
	onSave: (state: FormulaBuilderFormState & { dataTypeId: number }) => void;
	isSaving?: boolean;
	initialState?: Partial<FormulaBuilderFormState>;
	existingVariables?: RuleVariable[];
	editingVariableId?: string | null;
	operatorOptions?: Option[];
	functionOptions?: Option[];
	/** From rule builder (getFormMetadata); used when operatorOptions/functionOptions are not overridden. */
	formulaOperatorOptions?: Option[];
	expressionFunctionOptions?: Option[];
	isFormMetadataLoading?: boolean;
	/** Same resource wiring as DecisionForm Resource (global constants excluded). */
	platformId?: string | number;
	resourceVariableGroups?: VariableGroupV2[];
	formulaVariableOptions?: Option[];
	singleRowDatabaseVariableOptions?: Option[];
	resourceScreenFields?: Option[];
	resourceAdvancedTriggers?: Option[];
	/** Platform screen locations (same list as Create Rule modal). */
	screenLocationOptions?: ScreenLocationOption[];
	/** Rule's configured screen location — excluded from "Other Screens with Fields". */
	ruleScreenLocationId?: string | number;
}

const EMPTY_OPTIONS: Option[] = [];
const EMPTY_VARIABLE_GROUPS: VariableGroupV2[] = [];

function dedupeReactionResourceOptions(options: ReactionResourceOption[]): ReactionResourceOption[] {
	const byKey = new Map<string, ReactionResourceOption>();
	for (const o of options) {
		byKey.set(`${o.resourceType}:${o.value}`, o);
	}
	return [...byKey.values()];
}
const FormulaBuilderForm = (props: FormulaBuilderFormProps) => {
	const {
		open,
		onClose,
		onSave,
		isSaving = false,
		initialState,
		existingVariables = [],
		editingVariableId = null,
		operatorOptions,
		functionOptions,
		formulaOperatorOptions = [],
		expressionFunctionOptions: expressionFunctionOptionsProp = [],
		isFormMetadataLoading = false,
		platformId,
		resourceVariableGroups = EMPTY_VARIABLE_GROUPS,
		formulaVariableOptions = EMPTY_OPTIONS,
		singleRowDatabaseVariableOptions = EMPTY_OPTIONS,
		resourceScreenFields = EMPTY_OPTIONS,
		resourceAdvancedTriggers = EMPTY_OPTIONS,
		screenLocationOptions = [],
		ruleScreenLocationId,
	} = props;

	const authHeader = useAuthHeader();

	const [fetchedScreenFields, setFetchedScreenFields] = useState<Option[]>([]);
	const [fetchedDatabaseTables, setFetchedDatabaseTables] = useState<DatabaseTableOptionV2[]>([]);
	const [resourcePickerValue, setResourcePickerValue] = useState("");
	const [pickedResourceOptions, setPickedResourceOptions] = useState<ReactionResourceOption[]>([]);

	useEffect(() => {
		if (!open || !platformId) return;

		let cancelled = false;

		const fetchResourceOptions = async () => {
			try {
				const tablesResponse = await axios.get(apiRoutes.getTableOptions(platformId), { headers: authHeader });
				if (cancelled) return;
				const rawGroups = tablesResponse.data?.tableGroups ?? [];

				const screenTables: { id: number; name: string }[] = [];
				const databaseTables: { id: number; name: string }[] = [];

				for (const group of rawGroups) {
					if (group.screenTables) screenTables.push(...group.screenTables);
					if (group.databaseTables) databaseTables.push(...group.databaseTables);
				}

				const screenFieldResults = await Promise.all(
					screenTables.map(async (table) => {
						try {
							const res = await axios.get(apiRoutes.getScreenTableFields(table.id), { headers: authHeader });
							return (res.data ?? []).map((field: { id: number; name: string }) => ({
								label: `${table.name}.${field.name}`,
								value: String(field.id),
							}));
						} catch {
							return [];
						}
					}),
				);
				if (cancelled) return;
				setFetchedScreenFields(screenFieldResults.flat());

				const dbTableResults = await Promise.all(
					databaseTables.map(async (table) => {
						try {
							const res = await axios.get(apiRoutes.getDatabaseTableFields(table.id), { headers: authHeader });
							const fields: Option[] = (res.data ?? []).map((field: { id: number; name: string }) => ({
								label: field.name,
								value: String(field.id),
							}));
							return { label: table.name, value: String(table.id), fields };
						} catch {
							return { label: table.name, value: String(table.id), fields: [] };
						}
					}),
				);
				if (cancelled) return;
				setFetchedDatabaseTables(dbTableResults);
			} catch (error) {
				console.error("Error fetching resource options:", error);
			}
		};

		fetchResourceOptions();
		return () => { cancelled = true; };
	}, [open, platformId, authHeader]);

	const mergedScreenFields = useMemo(
		() => (resourceScreenFields.length ? resourceScreenFields : fetchedScreenFields),
		[resourceScreenFields, fetchedScreenFields],
	);

	const mergedDatabaseTables = fetchedDatabaseTables;

	const otherScreenLocations = useMemo((): ScreenLocationFieldOption[] => {
		if (!screenLocationOptions.length) return [];
		return screenLocationOptions
			.filter((loc) => String(loc.id) !== String(ruleScreenLocationId ?? ""))
			.map((loc) => ({ id: loc.id, name: loc.name }));
	}, [screenLocationOptions, ruleScreenLocationId]);

	const ruleScreenLocationName = useMemo(() => {
		const match = screenLocationOptions.find(
			(loc) => String(loc.id) === String(ruleScreenLocationId ?? ""),
		);
		return match?.name;
	}, [screenLocationOptions, ruleScreenLocationId]);

	const resourceVisibleGroups = useMemo((): VisibleGroupKey[] => {
		const keys: VisibleGroupKey[] = [];
		if (mergedScreenFields.length) keys.push("screenFields");
		if (resourceAdvancedTriggers.length) keys.push("advancedTriggers");
		const hasSimpleVariables = resourceVariableGroups.some((g) => g.options.length > 0);
		if (hasSimpleVariables) keys.push("simpleVariable");
		if (formulaVariableOptions.length) keys.push("formulaVariable");
		if (singleRowDatabaseVariableOptions.length) keys.push("singleRowDatabaseVariables");
		if (otherScreenLocations.length) keys.push("otherScreenLocations");
		return keys;
	}, [
		mergedScreenFields,
		resourceAdvancedTriggers.length,
		resourceVariableGroups,
		formulaVariableOptions.length,
		singleRowDatabaseVariableOptions.length,
		otherScreenLocations.length,
	]);

	const baseAvailableResourcesForEditor = useMemo((): ReactionResourceOption[] => {
		const fromSimple = resourceVariableGroups.flatMap((g) =>
			g.options.map((o) => ({ ...o, resourceType: "simpleVariable" as const })),
		);
		const fromDbTables = mergedDatabaseTables.flatMap((t) =>
			t.fields.map((o) => ({ ...o, resourceType: "databaseTableField" as const })),
		);
		return [
			...fromSimple,
			...formulaVariableOptions.map((o) => ({ ...o, resourceType: "formulaVariable" as const })),
			...singleRowDatabaseVariableOptions.map((o) => ({
				...o,
				resourceType: "databaseSingleRowVariable" as const,
			})),
			...mergedScreenFields.map((o) => ({ ...o, resourceType: "screenField" as const })),
			...resourceAdvancedTriggers.map((o) => ({
				label: o.label,
				value: o.value,
				triggerName: o.triggerName,
				dataType: o.dataType,
				resourceType: "advancedTrigger" as const,
			})),
			...fromDbTables,
		];
	}, [
		resourceVariableGroups,
		formulaVariableOptions,
		singleRowDatabaseVariableOptions,
		mergedScreenFields,
		resourceAdvancedTriggers,
		mergedDatabaseTables,
	]);

	const availableResourcesForEditor = useMemo(
		() => dedupeReactionResourceOptions([...baseAvailableResourcesForEditor, ...pickedResourceOptions]),
		[baseAvailableResourcesForEditor, pickedResourceOptions],
	);

	const resolvedOperatorOptions = useMemo(() => {
		const raw = operatorOptions?.length ? operatorOptions : formulaOperatorOptions;
		return dedupeOptionsByKey(raw, (o) => o.value);
	}, [operatorOptions, formulaOperatorOptions]);

	const functions = useMemo(() => {
		const raw = functionOptions?.length ? functionOptions : expressionFunctionOptionsProp;
		return dedupeOptionsByKey(raw, (o) => o.value);
	}, [functionOptions, expressionFunctionOptionsProp]);

	const buildInitialState = (): FormulaBuilderFormState => ({
		name: initialState?.name ?? "",
		dataTypeId: initialState?.dataTypeId ?? null,
		variableFormula: normalizeVariableFormulaFromApi(initialState?.variableFormula),
	});

	const initialFormState = useMemo(buildInitialState, [initialState]);
	const [formState, setFormState] = useState<FormulaBuilderFormState>(initialFormState);
	const [emptyNameError, setEmptyNameError] = useState(false);
	const [formulaValidationError, setFormulaValidationError] = useState(false);

	const textAreaRef = useRef<FormulaTextAreaHandle>(null);

	useEffect(() => {
		if (open) {
			setFormState(initialFormState);
			setEmptyNameError(false);
			setFormulaValidationError(false);
			setResourcePickerValue("");
			setPickedResourceOptions([]);
		}
	}, [initialFormState, open]);

	const duplicateNameMessage = useMemo(() => {
		const trimmed = formState.name.trim();
		if (!trimmed) {
			return null;
		}
		return isVariableNameTakenInRule(trimmed, existingVariables, editingVariableId)
			? "That name is already in use"
			: null;
	}, [editingVariableId, existingVariables, formState.name]);

	const nameHelperText = duplicateNameMessage ?? (emptyNameError ? "Enter a variable name" : null);
	const hasNameError = Boolean(duplicateNameMessage || emptyNameError);

	const plainFormula = formState.variableFormula.formula.trim();
	const isSaveDisabled =
		!formState.name.trim() ||
		Boolean(duplicateNameMessage) ||
		!formState.dataTypeId ||
		!plainFormula ||
		isSaving;

	const handleSave = () => {
		const trimmedName = formState.name.trim();
		if (!trimmedName) {
			setEmptyNameError(true);
			return;
		}

		if (isVariableNameTakenInRule(trimmedName, existingVariables, editingVariableId)) {
			return;
		}
		if (formState.dataTypeId == null) return;
		if (!formState.variableFormula.formula.trim()) {
			setFormulaValidationError(true);
			return;
		}

		setEmptyNameError(false);
		setFormulaValidationError(false);
		onSave({ ...formState, name: trimmedName, dataTypeId: formState.dataTypeId });
	};

	const handleOperatorChange = (value: string) => {
		if (!value) return;
		textAreaRef.current?.insertAtEnd(value);
	};

	const handleResourceEntityChange = (
		val: string,
		category: ResourceCategoryV2,
		label: string,
		_dataType?: string,
		locationName?: string,
	) => {
		if (category === "globalConstant") return;
		const insertLabel = getEditorInsertLabelForPickedResource(
			category,
			label,
			val,
			mergedScreenFields,
			resourceAdvancedTriggers,
			locationName,
			ruleScreenLocationName,
		);
		const advancedMatch =
			category === "advancedTrigger"
				? resourceAdvancedTriggers.find((t) => t.value === val)
				: undefined;
		const resource: ReactionResourceOption = {
			label: insertLabel,
			value: val,
			resourceType: category,
			...(advancedMatch?.triggerName ? { triggerName: advancedMatch.triggerName } : {}),
		};
		setPickedResourceOptions((previous) => dedupeReactionResourceOptions([...previous, resource]));
		textAreaRef.current?.insertAtCursor(resource);
		setResourcePickerValue("");
	};

	const handleFunctionSelect = (value: string) => {
		if (!value) return;
		const opt = functions.find((f) => f.value === value);
		const text = opt?.insertText ?? value;
		textAreaRef.current?.insertAtEnd(text);
	};

	const handleDataTypeChange = (id: number | null, _selectedOption?: VariableDataTypeOption | null) => {
		setFormState((previous) => ({ ...previous, dataTypeId: id }));
	};

	return (
		<BaseForm
			open={open}
			onClose={onClose}
			onSave={handleSave}
			title="Formula Variable"
			icon={<Calculate />}
			iconColor="#A434D9"
			gradient="linear-gradient(135deg, #D63AD9 0%, #8C30E0 100%)"
			isSaving={isSaving}
			isSaveDisabled={isSaveDisabled}
			saveButtonText="Save Formula Variable"
			width={520}
		>
			<Stack spacing={3}>
				<CanvasFormTextField
					fullWidth
					required
					label="Variable Name"
					value={formState.name}
					onChange={(e) => {
						setEmptyNameError(false);
						setFormState((p) => ({ ...p, name: e.target.value }));
					}}
					error={hasNameError}
					helperText={nameHelperText}
					width="100%"
				/>

				<DataTypeField
					value={formState.dataTypeId}
					onChange={handleDataTypeChange}
				/>

				<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
					<FormulaOperators
						onSelect={handleOperatorChange}
						options={resolvedOperatorOptions}
						suppressAutoFetch
						disabled={isFormMetadataLoading}
					/>
					{isFormMetadataLoading ? <CircularProgress size={22} /> : null}
				</Stack>
				<FormEntitySelect
					label="Resource"
					value={resourcePickerValue}
					ruleScreenLocationName={ruleScreenLocationName}
					onChange={(
						val: string,
						category: ResourceCategoryV2,
						label: string,
						dataType?: string,
						locationName?: string,
					) => {
						handleResourceEntityChange(val, category, label, dataType, locationName);
					}}
					variableGroups={resourceVariableGroups}
					formulaVariables={formulaVariableOptions}
					databaseSingleRowVariableOptions={singleRowDatabaseVariableOptions}
					screenFields={mergedScreenFields}
					advancedTriggers={resourceAdvancedTriggers}
					otherScreenLocations={otherScreenLocations}
					visibleGroups={resourceVisibleGroups}
				/>
				<CanvasFormTextField
					select
					fullWidth
					label="Function"
					value=""
					onChange={(e) => handleFunctionSelect(e.target.value)}
					width="100%"
					disabled={isFormMetadataLoading}
				>
					<MenuItem value="" disabled>
						Add Function
					</MenuItem>
					{functions.map((opt) => (
						<MenuItem key={opt.value} value={opt.value}>
							<Stack
								direction="row"
								spacing={1}
								sx={{ alignItems: "center", justifyContent: "space-between", width: "100%" }}
							>
								<span>{opt.label}</span>
								{opt.description ? (
									<Tooltip title={opt.description} arrow placement="left">
										<InfoOutlined
											fontSize="small"
											sx={{ color: "text.secondary", flexShrink: 0 }}
											aria-label="Function description"
										/>
									</Tooltip>
								) : null}
							</Stack>
						</MenuItem>
					))}
				</CanvasFormTextField>

				{/* QA: visual demarcation between the "setup" fields (name,
				    data type, resource, operators, functions) and the formula
				    composer itself — so the user reads them as two phases. */}
				<Divider
					sx={{
						mt: 0.5,
						mb: -0.5,
						// Bleed past the form body's 20px horizontal padding so the
						// line runs full-width to the panel edges (flush to the menu
						// on the left) instead of sitting inset.
						mx: -2.5,
						borderColor: "rgba(13, 31, 48, 0.08)",
					}}
				/>

				<InputLabel sx={{ mb: -1.5, fontWeight: 500 }}>
					Variable Formula
				</InputLabel>
				<FormulaTextArea
					ref={textAreaRef}
					value={formState.variableFormula.formula}
					resources={formState.variableFormula.resources}
					onChange={(payload) => {
						setFormulaValidationError(false);
						setFormState((p) => ({ ...p, variableFormula: payload }));
					}}
					availableResources={availableResourcesForEditor}
				/>
				{formulaValidationError ? (
					<FormHelperText error sx={{ mt: -1 }}>
						{messages.RULE_FORMULA_REQUIRED_ERROR_MSG}
					</FormHelperText>
				) : null}
			</Stack>
		</BaseForm>
	);
};

export default FormulaBuilderForm;
